import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Star, Loader2, RefreshCw, Activity, Clock, Award, ShieldAlert } from "lucide-react";
import { PageShell, PageHeader, StatCard, StatGrid, SectionCard, EmptyState, ActionButton, KeyValueRow } from "@/components/ds";

export default function DriverDetail() {
  const { driverId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/logibus/drivers/${driverId}/overview`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [driverId]);

  if (!data) return (
    <PageShell>
      <div className="flex items-center gap-2 text-zinc-500"><Loader2 className="animate-spin" size={16} /> Chargement du chauffeur…</div>
    </PageShell>
  );

  const d = data.driver;
  const score = data.score;
  const scoreTone = score >= 90 ? "success" : score >= 70 ? "warning" : "danger";

  return (
    <PageShell testId="driver-detail-page" dense>
      <Link to="/admin/drivers" data-testid="back-to-drivers" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-3">
        <ArrowLeft size={12} /> Retour Chauffeurs
      </Link>

      <PageHeader
        eyebrow="Fiche chauffeur"
        title={
          <span className="flex items-center gap-4">
            <span className="w-14 h-14 inline-flex items-center justify-center bg-zinc-900 text-white border-2 border-zinc-900 shadow-[3px_3px_0_0_#FFC60A]">
              <User size={24} />
            </span>
            <span className="truncate">{d.name || d.email}</span>
          </span>
        }
        subtitle={d.email}
        actions={
          <ActionButton variant="ghost" icon={loading ? null : RefreshCw} loading={loading} onClick={load} testId="driver-refresh">
            Recharger
          </ActionButton>
        }
      />

      <StatGrid cols={4} className="mb-5">
        <StatCard
          testId="kpi-driver-score"
          icon={Star}
          label="Score chauffeur"
          value={`${score} / 100`}
          tone={scoreTone}
        />
        <StatCard testId="kpi-aff-7d" icon={Activity} label="Affectations 7j" value={data.stats_7d.affectations} />
        <StatCard
          testId="kpi-inc-7d"
          icon={ShieldAlert}
          label="Incidents 7j"
          value={data.stats_7d.incidents}
          tone={data.stats_7d.incidents > 0 ? "danger" : "default"}
        />
        <StatCard
          testId="kpi-last-login"
          icon={Clock}
          label="Dernier login"
          value={data.last_login ? new Date(data.last_login).toLocaleString("fr-CH").slice(0, 16) : "Jamais"}
        />
      </StatGrid>

      <div className="grid lg:grid-cols-2 gap-4">
        <SectionCard testId="active-aff-section" tone="navy" icon={Activity} title="Affectation active" bodyClassName="p-3">
          {data.active_affectation ? (
            <div className="divide-y divide-zinc-100">
              <KeyValueRow label="Véhicule" value={data.active_vehicle?.plate || data.active_vehicle?.model || data.active_affectation.vehicle_id} />
              <KeyValueRow label="Tournée GTFS" value={<span className="font-mono text-xs">{data.active_affectation.trip_id}</span>} />
              <KeyValueRow label="Source" value={data.active_affectation.source} />
              <KeyValueRow label="Verrouillée" value={data.active_affectation.locked ? "🔒 Oui" : "Non"} />
              <KeyValueRow label="Créée le" value={data.active_affectation.created_at?.slice(0, 16).replace("T", " ")} />
            </div>
          ) : (
            <EmptyState dense icon={Activity} title="Aucune affectation active" description="Ce chauffeur n'est lié à aucun véhicule actuellement." />
          )}
        </SectionCard>

        <SectionCard testId="live-state-section" tone="yellow" icon={Award} title="État live SAEIV" bodyClassName="p-3">
          {data.live_state ? (
            <div className="divide-y divide-zinc-100">
              <KeyValueRow label="État exploitation" value={<span className="font-bold uppercase">{data.live_state.service_status}</span>} />
              {data.live_state.detected_route_short_name && <KeyValueRow label="Ligne détectée" value={data.live_state.detected_route_short_name} />}
              {data.live_state.next_stop_name && <KeyValueRow label="Prochain arrêt" value={data.live_state.next_stop_name} />}
              {data.live_state.delay_minutes != null && <KeyValueRow label="Retard" value={`${data.live_state.delay_minutes > 0 ? "+" : ""}${data.live_state.delay_minutes} min`} />}
              {data.live_state.speed_kmh != null && <KeyValueRow label="Vitesse" value={`${Math.round(data.live_state.speed_kmh)} km/h`} />}
              {data.live_state.gps_age_seconds != null && <KeyValueRow label="Dernier ping GPS" value={`${Math.floor(data.live_state.gps_age_seconds / 60)} min`} />}
            </div>
          ) : (
            <EmptyState dense icon={Award} title="Aucune donnée live" description="Le chauffeur n'est pas en service ou le GPS est offline." />
          )}
        </SectionCard>
      </div>
    </PageShell>
  );
}
